import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Table,
  Spinner,
  Link,
} from "@amzn/awsui-components-react";
import { getEmployeesByLoginList } from "../../utilities/papiAPIs";

// People data is resolved on the UI from the stored alias — only alias + role
// are persisted. The photo is the badge photo (same source the top nav uses);
// the name is resolved from the People API (PAPI), with the alias as a fallback.
const badgePhotoUrl = (alias) =>
  `https://badgephotos.corp.amazon.com/?uid=${encodeURIComponent(alias)}`;
const phonetoolUrl = (alias) =>
  `https://phonetool.amazon.com/users/${encodeURIComponent(alias)}`;

/**
 * Contact Us tab: a table of people to reach out to about RAISE — badge photo,
 * name (resolved from the alias via PAPI, linked to phonetool), and role. The
 * backend stores only alias + role; everything else is resolved here.
 */
const ContactUs = ({ contacts }) => {
  // alias (lowercased) -> "First Last", resolved from PAPI.
  const [namesByAlias, setNamesByAlias] = useState({});
  // Start in the resolving state when there are aliases to resolve, so the
  // loader shows on the very first render — otherwise the table would flash
  // raw aliases for one frame before the effect flips this to true.
  const [resolving, setResolving] = useState(
    () => (contacts || []).some((c) => c.alias)
  );

  const list = useMemo(
    () =>
      [...(contacts || [])].sort(
        (a, b) => (a.sort_order || 0) - (b.sort_order || 0)
      ),
    [contacts]
  );

  // Resolve display names for the contact aliases in one bulk PAPI call.
  useEffect(() => {
    const aliases = list.map((c) => c.alias).filter(Boolean);
    if (aliases.length === 0) return;
    let cancelled = false;
    setResolving(true);
    (async () => {
      try {
        const records = await getEmployeesByLoginList(aliases, aliases.length);
        if (cancelled) return;
        const map = {};
        (records || []).forEach((r) => {
          const bi = r.basicInfo || {};
          const login = (bi.login || "").toLowerCase();
          const name = [bi.firstName, bi.lastName].filter(Boolean).join(" ");
          if (login && name) map[login] = name;
        });
        setNamesByAlias(map);
      } catch (e) {
        // Non-fatal: fall back to showing the alias as the name.
      } finally {
        if (!cancelled) setResolving(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [list]);

  const displayName = (alias) =>
    namesByAlias[(alias || "").toLowerCase()] || alias;

  // Show a loader until contacts are fetched AND their names are resolved from
  // PAPI, so the table doesn't briefly flash raw aliases before the names land.
  if (!contacts || resolving) {
    return (
      <Box padding="l">
        <Spinner /> Loading contacts...
      </Box>
    );
  }

  return (
    <Table
      variant="embedded"
      items={list}
      trackBy="contact_id"
      empty={<Box variant="p">No contacts have been added yet.</Box>}
      header={
        <Box variant="p">
          Questions about RAISE? Reach out to the team below.
        </Box>
      }
      columnDefinitions={[
        {
          id: "photo",
          header: "",
          width: 72,
          minWidth: 72,
          cell: (c) => (
            // Same badge-photo source the top nav uses. No onError handler —
            // matching the top nav — so a slow/served image isn't hidden.
            <img
              className="rounded-circle"
              src={badgePhotoUrl(c.alias)}
              alt=""
              width={48}
              height={48}
              style={{
                borderRadius: "50%",
                objectFit: "cover",
                background: "#eee",
              }}
            />
          ),
        },
        {
          id: "name",
          header: "Name",
          cell: (c) => (
            <Link href={phonetoolUrl(c.alias)} external>
              {displayName(c.alias)}
            </Link>
          ),
        },
        {
          id: "role",
          header: "Role",
          cell: (c) => c.role || "",
        },
      ]}
    />
  );
};

export default ContactUs;
